import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  TextField,
  Grid,
  Link,
  Box,
  Tooltip,
  IconButton,
  CircularProgress,
  Paper,
  Typography,
  FormControl,
} from '@material-ui/core';
import { PageableTable } from 'components/PageableTable';
import { PageableTableHeader } from 'components/PageableTable/components';
import { DateDisplay } from 'components/DateDisplay/DateDisplay';
import GroupWorkIcon from '@material-ui/icons/GroupWork';
import { fetchTerms } from 'lib/Api/TermsGenerator';
import { TermsDocument, FetchTermsResponse } from 'lib/Model/TermsGenerator';
import {
  getTableSettingsStorageKey,
  useLocalStorageSettings,
  useDebounce,
  TableSettings,
} from 'hooks';
import RemoveRedEye from '@material-ui/icons/RemoveRedEye';
import HistoryIcon from '@material-ui/icons/History';
import { TermsDetailsDialog } from './TermsDetailsDialog';
import {
  getTermsStatus,
  TERMS_STATUSES,
  TermsStatusChip,
} from './TermsStatusChip';
import { Autocomplete } from '@material-ui/lab';
import { CompanyAssociationDialog } from './CompanyAssociationDialog';
import { Launch } from '@material-ui/icons';
import { TermsChangelog } from './TermsChangelog';

const useStyles = makeStyles((theme) => ({
  searchBox: {
    padding: theme.spacing(1),
  },
  tableContainer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    overflow: 'auto',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  errorMessage: {
    color: theme.palette.error.main,
    margin: theme.spacing(2),
    textAlign: 'center',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  actionButton: {
    padding: theme.spacing(0.5),
  },
  filterContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  switchContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    height: '100%',
  },
}));

interface TermsHistoryTableProps {
  companyId?: string;
  onLinkClick?: (termsId: number, companyName: string) => void;
  storageKeySuffix?: string;
}

export const TermsHistoryTable: React.FC<TermsHistoryTableProps> = ({
  companyId,
  onLinkClick,
  storageKeySuffix = 'global',
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [termsHistory, setTermsHistory] = useState<
    FetchTermsResponse | undefined
  >();
  const [pageNumber, setPageNumber] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [textSearch, setTextSearch] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [brandFilter, setBrandFilter] = useState<string | undefined>();
  const [countryFilter, setCountryFilter] = useState<string | undefined>();

  const debouncedTextSearch = useDebounce(textSearch, 400);

  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState<TermsDocument | undefined>();

  const [associationDialogOpen, setAssociationDialogOpen] = useState(false);
  const [termToLink, setTermToLink] = useState<{
    id: number;
    companyName: string;
  } | null>(null);
  const [zOpen, setZOpen] = useState(false);
  const [zId, setZId] = useState<number | null>(null);

  const storageKey = getTableSettingsStorageKey(
    'termsHistory',
    storageKeySuffix,
  );
  const [tableSettings, setTableSettings] =
    useLocalStorageSettings<TableSettings>(storageKey, { rowsPerPage: 10 });

  const fetchTermsData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchTerms({
        companyId,
        searchParam: debouncedTextSearch,
        page: pageNumber + 1,
        pageSize: tableSettings.rowsPerPage,
        status: statusFilter,
        brand: brandFilter,
        country: countryFilter,
      });
      setTermsHistory(data);
    } catch (err) {
      setError(
        `Failed to load terms documents: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
      );
    } finally {
      setLoading(false);
    }
  }, [
    companyId,
    debouncedTextSearch,
    pageNumber,
    tableSettings.rowsPerPage,
    statusFilter,
    brandFilter,
    countryFilter,
  ]);

  useEffect(() => {
    fetchTermsData();
  }, [fetchTermsData]);

  useEffect(() => {
    setPageNumber(0);
  }, [
    debouncedTextSearch,
    tableSettings.rowsPerPage,
    statusFilter,
    brandFilter,
    countryFilter,
  ]);

  const handleViewDetails = (term: TermsDocument) => {
    setSelectedTerm(term);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedTerm(undefined);
  };

  const handleCloseAssociationDialog = () => {
    setAssociationDialogOpen(false);
    setTermToLink(null);
  };

  const handleAssociationSuccess = () => {
    fetchTermsData();
  };

  const historyColumns: PageableTableHeader[] = [
    {
      key: 'status',
      label: 'Status',
      sortable: false,
      props: { align: 'center', style: { width: '120px' } },
    },
    {
      key: 'created_on',
      label: 'Created On',
      sortable: false,
      props: { align: 'center' },
    },
    {
      key: 'expires_on',
      label: 'Expires On',
      sortable: false,
      props: { align: 'center' },
    },
    {
      key: 'brand',
      label: 'Brand',
      sortable: false,
      props: { align: 'center' },
    },
    {
      key: 'country',
      label: 'Country',
      sortable: false,
      props: { align: 'center' },
    },
    {
      key: 'company_name',
      label: 'Company Name',
      sortable: false,
      props: { align: 'center' },
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      props: { align: 'center', style: { width: '100px' } },
    },
    {
      key: 'history',
      label: 'History',
      sortable: false,
      props: { align: 'center', style: { width: '80px' } },
    },
    {
      key: 'folder',
      label: 'Folder',
      sortable: false,
      props: { align: 'center', style: { width: '100px' } },
    },
  ];

  return (
    <>
      <Grid container spacing={2} alignItems="center">
        {!companyId && (
          <Grid item xs={12} md={4}>
            <TextField
              variant="outlined"
              placeholder="Search company or ID"
              value={textSearch ?? ''}
              className={classes.searchBox}
              onChange={(e) => setTextSearch(e.target.value || undefined)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
          </Grid>
        )}

        <Grid item xs={12} md={companyId ? 4 : 3}>
          <FormControl
            variant="outlined"
            fullWidth
            className={classes.searchBox}
          >
            <Autocomplete
              id="status-filter"
              options={['All', ...TERMS_STATUSES]}
              getOptionLabel={(option) => option}
              value={statusFilter || 'All'}
              onChange={(event, newValue) => {
                setStatusFilter(
                  newValue === 'All' ? undefined : newValue || undefined,
                );
              }}
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Status"
                  placeholder="Filter by status"
                  InputLabelProps={{ shrink: true }}
                  disabled={loading}
                />
              )}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={companyId ? 4 : 3}>
          <FormControl
            variant="outlined"
            fullWidth
            className={classes.searchBox}
          >
            <Autocomplete
              id="brand-filter"
              options={['All', 'Konnekt', 'Talexio', 'SIM']}
              getOptionLabel={(option) => option}
              value={brandFilter || 'All'}
              onChange={(event, newValue) => {
                setBrandFilter(
                  newValue === 'All' ? undefined : newValue || undefined,
                );
              }}
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Brand"
                  placeholder="Filter by brand"
                  InputLabelProps={{ shrink: true }}
                  disabled={loading}
                />
              )}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={companyId ? 4 : 2}>
          <FormControl
            variant="outlined"
            fullWidth
            className={classes.searchBox}
          >
            <Autocomplete
              id="country-filter"
              options={['All', 'Malta', 'Cyprus']}
              getOptionLabel={(option) => option}
              value={countryFilter || 'All'}
              onChange={(event, newValue) => {
                setCountryFilter(
                  newValue === 'All' ? undefined : newValue || undefined,
                );
              }}
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Country"
                  placeholder="Filter by country"
                  InputLabelProps={{ shrink: true }}
                  disabled={loading}
                />
              )}
            />
          </FormControl>
        </Grid>
      </Grid>

      {loading && !termsHistory ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : error ? (
        <Typography variant="body1" className={classes.errorMessage}>
          {error}
        </Typography>
      ) : termsHistory?.data && termsHistory.data.length > 0 ? (
        <Paper className={classes.tableContainer}>
          <PageableTable
            tableSettings={tableSettings}
            setTableSettings={setTableSettings}
            showSkeletonLoading={loading}
            hideColumns={false}
            rows={
              termsHistory.data.map((term: TermsDocument, index: number) => {
                const status = getTermsStatus(term);

                return {
                  key: index.toString(),
                  cells: [
                    {
                      key: 'status',
                      display: <TermsStatusChip status={status} />,
                      props: {
                        align: 'center',
                      },
                    },
                    {
                      key: 'created_on',
                      display: term.created_on && (
                        <DateDisplay date={term.created_on} />
                      ),
                      props: { align: 'center' },
                    },
                    {
                      key: 'expires_on',
                      display: term.terms_expiry_date ? (
                        <DateDisplay date={term.terms_expiry_date} />
                      ) : (
                        '-'
                      ),
                      props: { align: 'center' },
                    },
                    {
                      key: 'brand',
                      display: term.terms_brand,
                      props: { align: 'center' },
                    },
                    {
                      key: 'country',
                      display: term.country || '-',
                      props: { align: 'center' },
                    },
                    {
                      key: 'company_name',
                      display: (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {term.consolidated_group_id && (
                            <Tooltip title="This is part of a consolidated agreement group.">
                              <GroupWorkIcon fontSize="small" color="action" />
                            </Tooltip>
                          )}
                          {term.company_id ? (
                            <Link
                              href={`${window.location.origin}/clients/${term.company_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="primary"
                            >
                              {term.company_name}
                            </Link>
                          ) : (
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              width="100%"
                            >
                              {term.company_name}
                            </Box>
                          )}
                        </Box>
                      ),
                      props: { align: 'center' },
                    },
                    {
                      key: 'actions',
                      display: (
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            className={classes.actionButton}
                            onClick={() => handleViewDetails(term)}
                          >
                            <RemoveRedEye fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                      props: { align: 'center' },
                    },
                    {
                      key: 'history',
                      display: (
                        <Tooltip title="View History">
                          <IconButton
                            size="small"
                            className={classes.actionButton}
                            onClick={() => {
                              setZId(term.id);
                              setZOpen(true);
                            }}
                          >
                            <HistoryIcon />
                          </IconButton>
                        </Tooltip>
                      ),
                      props: { align: 'center' },
                    },
                    {
                      key: 'folder',
                      display: term.folder_id ? (
                        <Link
                          href={`https://drive.google.com/drive/folders/${term.folder_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="primary"
                        >
                          <Launch>
                            <Typography variant="caption">
                              Open folder
                            </Typography>
                          </Launch>
                        </Link>
                      ) : (
                        '-'
                      ),
                      props: { align: 'center' },
                    },
                  ],
                };
              }) || []
            }
            loading={loading}
            pageNumber={pageNumber}
            rowCount={termsHistory?.total_count ?? 0}
            columns={historyColumns}
            onChangePage={setPageNumber}
          />
        </Paper>
      ) : (
        <div className={classes.emptyState}>
          <Typography variant="body1">
            {companyId ? (
              <>
                No generated agreement documents found for this company.
                <br />
                <Typography
                  variant="body1"
                  component="span"
                  style={{ fontWeight: 'bold' }}
                >
                  This does not necessarily mean there are no agreements, but
                  that none have been generated through the intranet yet.
                </Typography>
              </>
            ) : (
              'No generated agreement documents found.'
            )}
            {!companyId && statusFilter && (
              <>
                <br />
                <br />
                Try changing the status filter or search criteria.
              </>
            )}
            {!companyId && !statusFilter && (
              <>
                <br />
                <br />
                This does not necessarily mean there are no agreements, but that
                none have been generated through the intranet yet.
              </>
            )}
          </Typography>
        </div>
      )}
      <TermsDetailsDialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        term={selectedTerm}
        onSaved={fetchTermsData}
      />

      {zId !== null && (
        <TermsChangelog
          open={zOpen}
          onClose={() => setZOpen(false)}
          termsId={zId}
        />
      )}

      {/* Add Company Association Dialog */}
      {termToLink && (
        <CompanyAssociationDialog
          open={associationDialogOpen}
          onClose={handleCloseAssociationDialog}
          onSuccess={handleAssociationSuccess}
          termsId={termToLink.id}
          termsCompanyName={termToLink.companyName}
        />
      )}
    </>
  );
};
